import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';

import Button from 'sushiweb/Button';
import Modal from 'sushiweb/Modal/Modal';
import Layout from 'sushiweb/Layout';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import ChevronLeft from 'sushiweb/Icons/all/ChevronLeft';
import ChevronRight from 'sushiweb/Icons/all/ChevronRight';

import {
  PageWrapper,
  PageHeaderContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  SectionHeading,
  ContentContainer,
  FooterWrapper,
  FullFooter,
  PolicyWrapper,
  PolicyBox,
  PolicyDesc,
  PolicyListContainer,
  PolicyHeaderContainer,
  PolicyItemContainer,
  PolicyButton,
  SpinnerCenterWrapper,
  ModalFormWrapper,
} from '../../styles';

import { validateSlotsProgramStartTime } from './createPolicyForm/util';
import PolicySnippet from '../../common/Components/PolicySnippet/PolicySnippet';
import ConnectedCreatePolicyForm from './createPolicyForm';
import StepBar from '../../common/Components/StepBar/StepBar';
import { createPlanSteps } from '../../common/Components/constants';
import { CircledPlusFilledIcon } from '../../../../components/SVGs/commonSVGs';
import { transformProgramInfoToPolicyProps } from '../../common/helpers/inputHandlers';
import {
  needsInitialFetch,
  hasOptionOperationCompleted,
  createOptionDeletePayload,
  getModalTitle,
  getOptionsLength,
  getAllSlotsFromProgramInfo,
} from './util';
import { REVIEW_PLAN_ROUTE, CREATE_PLAN_EDIT_ROUTE } from '../../../../constants/routes';


function CreatePolicy({
  companyId,
  programInfo,
  fetchingProgramInfo,
  getProgramInfo,
  selectedWorkspace,
  updateOptionStatus,
  addingOption,
  addedOption,
  updatingOption,
  updatedOption,
  updatingOptionStatus,
  updatedOptionStatus,
  showErrorToast,
}) {
  const navigate = useNavigate();
  const { programId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showInnerModal, setShowInnerModal] = useState(false);

  useEffect(() => {
    if (programId && programId !== '' && selectedWorkspace?.workspace_id) {
      const needsInitial = needsInitialFetch(fetchingProgramInfo, programInfo);
      const operationCompleted = hasOptionOperationCompleted(
        addingOption,
        addedOption,
        updatingOption,
        updatedOption,
        updatingOptionStatus,
        updatedOptionStatus,
      );

      if (needsInitial || operationCompleted) {
        const timeoutId = setTimeout(() => {
          getProgramInfo({
            programId,
            companyId,
            workspaceId: selectedWorkspace?.workspace_id,
          });
        }, 300);

        return () => clearTimeout(timeoutId);
      }
    }

    return undefined;
  }, [
    programId,
    companyId,
    selectedWorkspace?.workspace_id,
    addingOption,
    addedOption,
    updatingOption,
    updatedOption,
    updatingOptionStatus,
    updatedOptionStatus,
  ]);

  const validateSlotTimings = () => {
    if (!programInfo?.start_time || !programInfo?.option_details?.length) {
      return true;
    }

    const allSlots = getAllSlotsFromProgramInfo(programInfo);
    const programStartTime = programInfo?.start_time ?? '00:00';

    if (allSlots.length > 0) {
      const isValid = validateSlotsProgramStartTime(allSlots, programStartTime);
      if (!isValid) {
        showErrorToast(
          'Slot level time range should be within the plan time range',
        );
        return false;
      }
    }
    return true;
  };

  const deleteOption = optionId => () => {
    const optionDeletePayload = createOptionDeletePayload(
      companyId,
      programInfo,
      optionId,
    );
    updateOptionStatus(optionDeletePayload);
  };

  const goBack = () => {
    navigate(CREATE_PLAN_EDIT_ROUTE.replace(':programId', programId));
  };

  const handleNextBtnClick = () => {
    const isValidTimings = validateSlotTimings();
    if (isValidTimings) {
      navigate(REVIEW_PLAN_ROUTE.replace(':programId', programId));
    }
  };

  const renderModal = () => {
    const modalTitle = getModalTitle(editingPolicy);
    return (
      <Modal
        title={modalTitle}
        visible={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingPolicy(null);
        }}
        type='flex'
        isCentered
        borderRadius='16px'
      >
        <ModalFormWrapper>
          <ConnectedCreatePolicyForm
            optionData={editingPolicy}
            onClose={() => {
              setShowModal(false);
              setEditingPolicy(null);
            }}
          />
        </ModalFormWrapper>
      </Modal>
    );
  };

  const renderEmptyPolicyPage = () => (
    <PolicyWrapper>
      <SectionHeading>Setup food policy</SectionHeading>
      <PolicyBox>
        {fetchingProgramInfo ? (
          <SpinnerCenterWrapper>
            <Spinner color={red.z_red} size='small' />
          </SpinnerCenterWrapper>
        ) : (
          <>
            <PolicyDesc>
              Ensure your employees comply to your company&apos;s food policy
            </PolicyDesc>
            <PolicyButton
              onClick={() => setShowModal(true)}
              size='medium'
              appearance='link'
              icon={<CircledPlusFilledIcon />}
            >
              Add policy
            </PolicyButton>
          </>
        )}
      </PolicyBox>
    </PolicyWrapper>
  );

  const renderHeaderButtons = () => (
    <Layout.Item colD={2} colT={12} colM={12} style={{ textAlign: 'right' }}>
      <PolicyButton
        onClick={() => {
          setEditingPolicy(null);
          setShowModal(true);
        }}
        size='small'
        appearance='link'
        icon={<CircledPlusFilledIcon />}
      >
        Add new policy
      </PolicyButton>
    </Layout.Item>
  );

  const renderPolicies = () => {
    const optionsLength = getOptionsLength(programInfo);
    if (optionsLength === 0) return renderEmptyPolicyPage();

    const policyPropsArray = transformProgramInfoToPolicyProps({
      apiData: programInfo,
      setEditingPolicy,
      setShowModal,
      deleteOption,
      isViewMode: false,
    });

    return (
      <PolicyListContainer>
        <PolicyHeaderContainer>
          <Layout.Item colD={8} colT={12} colM={12} style={{ textAlign: 'left' }}>
            <SectionHeading>Define food policy</SectionHeading>
          </Layout.Item>
          {renderHeaderButtons()}
        </PolicyHeaderContainer>
        {policyPropsArray.map(policyProps => (
          <PolicyItemContainer key={`policy-${policyProps?.policyTitle}`}>
            <PolicySnippet
              policyTitle={policyProps?.policyTitle}
              buttons={policyProps?.buttons}
              applicableServices={policyProps?.applicableServices}
              applicableDays={policyProps?.applicableDays}
              budgetPerEmployee={policyProps?.budgetPerEmployee}
              cycleType={policyProps?.cycleType}
              slots={policyProps?.slots}
              policyQuestions={policyProps?.policyQuestions}
              onModalOpen={() => setShowInnerModal(true)}
              onModalClose={() => setShowInnerModal(false)}
            />
          </PolicyItemContainer>
        ))}
      </PolicyListContainer>
    );
  };

  return (
    <PageWrapper>
      <PageHeaderContainer>
        <PageHeader>
          <PageTitle>Create meal plan</PageTitle>
          <PageSubtitle>Set up a new meal plan for your teams</PageSubtitle>
        </PageHeader>
        <StepBar steps={createPlanSteps} currentStep={2} />
      </PageHeaderContainer>
      <ContentContainer>
        {showModal ? renderModal() : renderPolicies()}
      </ContentContainer>
      {!showModal && !showInnerModal && (
        <FooterWrapper>
          <FullFooter>
            <Button
              appearance='outline'
              icon={<ChevronLeft />}
              iconAlign='left'
              onClick={goBack}
            >
              Back
            </Button>
            <Button
              iconAlign='right'
              icon={<ChevronRight />}
              btnColor='red'
              onClick={handleNextBtnClick}
              disabled={getOptionsLength(programInfo) === 0}
            >
              Review
            </Button>
          </FullFooter>
        </FooterWrapper>
      )}
    </PageWrapper>
  );
}

CreatePolicy.propTypes = {
  companyId: PropTypes.string,
  programInfo: PropTypes.object,
  getProgramInfo: PropTypes.func.isRequired,
  selectedWorkspace: PropTypes.object,
  updateOptionStatus: PropTypes.func.isRequired,
  fetchingProgramInfo: PropTypes.bool,
  addingOption: PropTypes.bool,
  addedOption: PropTypes.bool,
  updatingOption: PropTypes.bool,
  updatedOption: PropTypes.bool,
  updatingOptionStatus: PropTypes.bool,
  updatedOptionStatus: PropTypes.bool,
  showErrorToast: PropTypes.func,
};

CreatePolicy.defaultProps = {
  companyId: '',
  selectedWorkspace: {},
  programInfo: {},
  fetchingProgramInfo: true,
  addingOption: false,
  addedOption: true,
  updatingOption: false,
  updatedOption: true,
  updatingOptionStatus: false,
  updatedOptionStatus: true,
  showErrorToast: _noop,
};

export default CreatePolicy;
